/*
 * Copyright (c) 2025 Amazon.com, Inc. or its affiliates. All rights reserved.
 *
 * PROPRIETARY/CONFIDENTIAL. USE IS SUBJECT TO LICENSE TERMS.
 */

import {SUPPORTED_FILE_TYPES} from 'src/photoForm/validation';
import type {ComponentProps} from 'react';
import * as styles from './styles/FileInput.module.scss';

export type FileInputProps = Omit<
  ComponentProps<'input'>,
  'accept' | 'className' | 'multiple' | 'onClick' | 'type'
>;

/**
 * An invisible input configured for photo selection. Should be wrapped with a
 * user-visible component to show the actual input UI.
 */
export const FileInput = (props: FileInputProps) => {
  return (
    <input
      className={styles.invisibleInput}
      type="file"
      // Force our input elements to offer the choice of selecting a photo from
      // the gallery or (on mobile) taking a photo with the camera. There are
      // multiple caveats due to inconsistent platform behavior:
      // 1. On recent Android versions, the option to take a picture with the
      //    camera requires adding an invalid file type to the accept prop.
      //    Without the additional file type, Android users would only see the
      //    option to pick from the gallery.
      //    See: https://blog.addpipe.com/html-file-input-accept-video-camera-option-is-missing-android-14-15/
      // 2. On older Android versions (verified with Android 10 and Chrome 91),
      //    the file picker does not seem to enforce the file types we give to
      //    the accept prop. This means the user can upload any image (i.e. WebP)
      //    and will thus see an error. We don't have a great error message for
      //    this at the time (https://issues.labcollab.net/browse/FTVMOD-14653).
      //    This is not an issue on newer Android versions.
      accept={`${SUPPORTED_FILE_TYPES.join(',')},android/force-camera-workaround`}
      multiple
      onClick={(event) => {
        // Reset value so that the user can pick the same file back-to-back.
        // This is useful in some scenarios - for example: User picks photo
        // > user removes photo > user changes mind and reselects same photo
        (event.target as HTMLInputElement).value = '';
      }}
      {...props}
    />
  );
};
